import React, { useContext, useEffect, useRef, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import "../../../assets/css/gcstaff/staffdashboard/TeacherDashboardPage.css";

import axios from "axios";
import AppContext from "../../../context/AppContext";

/* ── Small inline icon set ── */
const Icon = ({ name, ...props }) => {
  const common = {
    width: 22, height: 22, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: 1.8,
    strokeLinecap: "round", strokeLinejoin: "round", ...props,
  };
  switch (name) {
    case "book":
      return <svg {...common}><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>;
    case "bookmark":
      return <svg {...common}><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="9" y1="12" x2="12" y2="12"/></svg>;
    case "doc":
      return <svg {...common}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>;
    case "upload":
      return <svg {...common}><path d="M12 16V4"/><path d="M7 9l5-5 5 5"/><path d="M4 16v3a2 2 0 002 2h12a2 2 0 002-2v-3"/></svg>;
    case "spark":
      return <svg {...common}><path d="M12 2l1.6 5.6L19 9l-5.4 1.4L12 16l-1.6-5.6L5 9l5.4-1.4z"/></svg>;
    case "bell":
      return <svg {...common}><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
    case "alert":
      return <svg {...common}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
    case "clock":
      return <svg {...common} width={16} height={16}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case "trendUp":
      return <svg {...common} width={12} height={12} strokeWidth={2.4}><polyline points="3 17 9 11 13 15 21 7"/><polyline points="14 7 21 7 21 14"/></svg>;
    case "chat":
      return <svg {...common}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
    default:
      return null;
  }
};

/* ── Quick Actions (colours match reference: blue / green / purple / yellow) ── */
const QUICK_ACTIONS = [
  { id: 1, label: "Browse Library", icon: "book",   href: "/library",           colorClass: "blue"   },
  { id: 2, label: "Upload Notes",   icon: "upload", href: "/teacher/my-notes",  colorClass: "green"  },
  { id: 3, label: "AI Assistant",   icon: "spark",  href: "/teacher/libraro",   colorClass: "purple" },
  { id: 4, label: "Saved PDFs",     icon: "doc",    href: "/teacher/saved-pdfs",colorClass: "yellow" },
];

/* ── Animated Counter ── */
const Counter = ({ target, visible }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!visible) return;
    let n = 0;
    const step = Math.ceil((target || 0) / 40) || 1;
    const t = setInterval(() => {
      n += step;
      if (n >= target) { setVal(target); clearInterval(t); }
      else setVal(n);
    }, 18);
    return () => clearInterval(t);
  }, [visible, target]);
  return <>{val}</>;
};

/* ── Status badge colour mapping for disputes ── */
const DISPUTE_STATUS_CLASS = {
  open:        "gcTD-days-badge--orange",
  pending:     "gcTD-days-badge--orange",
  resolved:    "gcTD-days-badge--green",
  approved:    "gcTD-days-badge--green",
  "in review": "gcTD-days-badge--blue",
  rejected:    "gcTD-days-badge--red",
};

/* ══════════════════════════════════════════
   Main Page
══════════════════════════════════════════ */
const TeacherDashboardPage = () => {
  const [pageIn,  setPageIn]  = useState(false);
  const [statsIn, setStatsIn] = useState(false);
  const [qaIn,    setQaIn]    = useState(false);
  const [alertIn, setAlertIn] = useState(false);
  const [cardsIn, setCardsIn] = useState(false);

  /* ── Dynamic data state ── */
  const [stats, setStats] = useState({
    totalIssued: 0,
    totalReserved: 0,
    totalNotesUploaded: 0,
  });
  const [disputes, setDisputes]               = useState([]);
  const [returnAlerts, setReturnAlerts]        = useState([]);
  const [statsLoading, setStatsLoading]        = useState(true);
  const [disputesLoading, setDisputesLoading]  = useState(true);
  const [alertsLoading, setAlertsLoading]      = useState(true);

  const statsRef = useRef(null);
  const qaRef    = useRef(null);
  const alertRef = useRef(null);
  const cardsRef = useRef(null);

  /* ── All from AppContext — same pattern as Student & Admin Dashboard ── */
  const { loggedInUser, baseUrl, authToken } = useContext(AppContext) || {};
  const teacherName = loggedInUser?.name || loggedInUser?.fullName || loggedInUser?.username || "Teacher";

  /* ── Page mount animation ── */
  useEffect(() => { setTimeout(() => setPageIn(true), 50); }, []);

  /* ── Intersection observers ── */
  useEffect(() => {
    const mkObs = (setter) =>
      new IntersectionObserver(([e]) => { if (e.isIntersecting) setter(true); }, { threshold: 0.1 });
    const s = mkObs(setStatsIn); if (statsRef.current) s.observe(statsRef.current);
    const q = mkObs(setQaIn);    if (qaRef.current)    q.observe(qaRef.current);
    const a = mkObs(setAlertIn); if (alertRef.current) a.observe(alertRef.current);
    const c = mkObs(setCardsIn); if (cardsRef.current) c.observe(cardsRef.current);
    return () => { s.disconnect(); q.disconnect(); a.disconnect(); c.disconnect(); };
  }, []);

  /* ── Fetch dashboard stats ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setStatsLoading(false); return; }

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/dashboard/fetchTeacherStatsForDashboard`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setStats({
            totalIssued:        res.data.totalIssued        ?? 0,
            totalReserved:      res.data.totalReserved      ?? 0,
            totalNotesUploaded: res.data.totalNotesUploaded ?? 0,
          });
        }
      })
      .catch((err) => console.error("Failed to fetch teacher stats:", err))
      .finally(() => setStatsLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Fetch return date alerts ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setAlertsLoading(false); return; }

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/dashboard/fetchTeacherReturnAlerts`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setReturnAlerts(res.data.alerts ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch return alerts:", err))
      .finally(() => setAlertsLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Fetch recent disputes ── */
  useEffect(() => {
    if (!loggedInUser?.id) { setDisputesLoading(false); return; }

    const formData = new FormData();
    formData.append("lbteacher_id", loggedInUser.id);

    axios
      .post(`${baseUrl}/teacher/dashboard/fetchTeacherRecentDisputes`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.success) {
          setDisputes(res.data.disputes ?? []);
        }
      })
      .catch((err) => console.error("Failed to fetch teacher disputes:", err))
      .finally(() => setDisputesLoading(false));
  }, [baseUrl, authToken, loggedInUser]);

  /* ── Format date nicely ── */
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  };

  return (
    <div className={`gcTD-page ${pageIn ? "gcTD-page--in" : ""}`}>
      <Container fluid className="gcTD-container">

        {/* ── Hero / Welcome Banner ── */}
        <div className="gcTD-hero">
          <div className="gcTD-hero__left">
            <span className="gcTD-hero__badge">
              <Icon name="spark" width={14} height={14} />
              Teacher Dashboard
            </span>
            <h2 className="gcTD-hero__title">Welcome back, {teacherName}!</h2>
            <p className="gcTD-hero__sub">Manage your resources and inspire learning today.</p>
          </div>
          <Link to="/teacher/my-notes" className="gcTD-hero__btn">
            Upload Notes <span>→</span>
          </Link>
        </div>

        {/* ── Stat Cards (Dynamic) ── */}
        <Row className="gy-3 gx-3 mb-3" ref={statsRef}>

          {/* Books Issued */}
          <Col xs={12} md={4}>
            <div className={`gcTD-stat ${statsIn ? "gcTD-stat--in" : ""}`} style={{ transitionDelay: "0.04s" }}>
              <div className="gcTD-stat__info">
                <p className="gcTD-stat__label">Books Issued</p>
                <h3 className="gcTD-stat__value">
                  {statsLoading ? "—" : <Counter target={stats.totalIssued} visible={statsIn} />}
                </h3>
                <p className="gcTD-stat__trend gcTD-stat__trend--up">
                  <Icon name="trendUp" /> Currently issued
                </p>
              </div>
              <div className="gcTD-stat__icon gcTD-stat__icon--teal">
                <Icon name="book" />
              </div>
            </div>
          </Col>

          {/* Reservations */}
          <Col xs={12} md={4}>
            <div className={`gcTD-stat ${statsIn ? "gcTD-stat--in" : ""}`} style={{ transitionDelay: "0.12s" }}>
              <div className="gcTD-stat__info">
                <p className="gcTD-stat__label">Reservations</p>
                <h3 className="gcTD-stat__value">
                  {statsLoading ? "—" : <Counter target={stats.totalReserved} visible={statsIn} />}
                </h3>
                <p className="gcTD-stat__trend gcTD-stat__trend--neutral">
                  <Icon name="trendUp" /> Pending approval
                </p>
              </div>
              <div className="gcTD-stat__icon gcTD-stat__icon--orange">
                <Icon name="bookmark" />
              </div>
            </div>
          </Col>

          {/* Notes Uploaded */}
          <Col xs={12} md={4}>
            <div className={`gcTD-stat ${statsIn ? "gcTD-stat--in" : ""}`} style={{ transitionDelay: "0.20s" }}>
              <div className="gcTD-stat__info">
                <p className="gcTD-stat__label">Notes Uploaded</p>
                <h3 className="gcTD-stat__value">
                  {statsLoading ? "—" : <Counter target={stats.totalNotesUploaded} visible={statsIn} />}
                </h3>
                <p className="gcTD-stat__trend gcTD-stat__trend--up">
                  <Icon name="trendUp" /> Total uploaded
                </p>
              </div>
              <div className="gcTD-stat__icon gcTD-stat__icon--green">
                <Icon name="doc" />
              </div>
            </div>
          </Col>
        </Row>

        {/* ── Quick Actions (coloured icons, matches reference) ── */}
        <div className={`gcTD-qa ${qaIn ? "gcTD-qa--in" : ""}`} ref={qaRef}>
          <h6 className="gcTD-qa__title">Quick Actions</h6>
          <div className="gcTD-qa__grid">
            {QUICK_ACTIONS.map((action, i) => (
              <Link
                key={action.id}
                to={action.href}
                className={`gcTD-qa__btn ${qaIn ? "gcTD-qa__btn--in" : ""}`}
                style={{ transitionDelay: `${0.08 + i * 0.07}s` }}
              >
                <span className={`gcTD-qa__icon gcTD-qa__icon--${action.colorClass}`}>
                  <Icon name={action.icon} width={22} height={22} strokeWidth={1.8} />
                </span>
                <span className="gcTD-qa__label">{action.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* ── Bottom Row: Alerts + Recent Disputes ── */}
        <Row className="gy-3 gx-3">

          {/* ── Return Date Alerts (Dynamic) ── */}
          <Col xs={12} md={6}>
            <div
              ref={alertRef}
              className={`gcTD-card ${alertIn ? "gcTD-card--in" : ""}`}
              style={{ transitionDelay: "0.08s" }}
            >
              <div className="gcTD-card__header">
                <span className="gcTD-card__header-icon"><Icon name="bell" width={17} height={17} /></span>
                <h6 className="gcTD-card__title">Return Date Alerts</h6>
              </div>

              <div className="gcTD-alerts">
                {alertsLoading ? (
                  <p className="gcTD-chat-date" style={{ padding: "16px 20px" }}>Loading return alerts...</p>
                ) : returnAlerts.length === 0 ? (
                  <p className="gcTD-chat-date" style={{ padding: "16px 20px" }}>No books currently issued.</p>
                ) : (
                  returnAlerts.map((a, i) => (
                    <div
                      key={a.id}
                      className={`gcTD-alert-row ${alertIn ? "gcTD-alert-row--in" : ""}`}
                      style={{ transitionDelay: `${0.15 + i * 0.1}s` }}
                    >
                      <div className={`gcTD-alert-clock ${a.type === "success" ? "gcTD-alert-clock--green" : "gcTD-alert-clock--orange"}`}>
                        <Icon name="clock" />
                      </div>
                      <div className="gcTD-alert-info">
                        <p className="gcTD-alert-title">{a.title}</p>
                        <p className="gcTD-alert-due">Due: {formatDate(a.due)}</p>
                      </div>
                      <span className={`gcTD-days-badge ${a.type === "success" ? "gcTD-days-badge--green" : a.type === "danger" ? "gcTD-days-badge--red" : "gcTD-days-badge--orange"}`}>
                        {a.badge}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Col>

          {/* ── Recent Disputes (Dynamic — design matches Student dashboard) ── */}
          <Col xs={12} md={6}>
            <div
              ref={cardsRef}
              className={`gcTD-card ${cardsIn ? "gcTD-card--in" : ""}`}
              style={{ transitionDelay: "0.16s" }}
            >
              <div className="gcTD-card__header">
                <span className="gcTD-card__header-icon"><Icon name="alert" width={16} height={16} /></span>
                <h6 className="gcTD-card__title">Recent Disputes</h6>
                <Link to="/teacher/disputes" className="gcTD-card__viewall">View All →</Link>
              </div>

              <div className="gcTD-chats">
                {disputesLoading ? (
                  <p className="gcTD-chat-date" style={{ padding: "16px 20px" }}>Loading disputes...</p>
                ) : disputes.length === 0 ? (
                  <p className="gcTD-chat-date" style={{ padding: "16px 20px" }}>No recent disputes found.</p>
                ) : (
                  disputes.map((d, i) => (
                    <div
                      key={d.id}
                      className={`gcTD-dispute-row ${cardsIn ? "gcTD-dispute-row--in" : ""}`}
                      style={{ transitionDelay: `${0.22 + i * 0.1}s` }}
                    >
                      <div className="gcTD-dispute-left">
                        <div className="gcTD-dispute-icon">
                          <Icon name="alert" width={14} height={14} strokeWidth={2} />
                        </div>
                        <div>
                          <div className="gcTD-dispute-head">
                            <span className="gcTD-dispute-subject">{d.title}</span>
                            <span className="gcTD-dispute-ticket">
                              #DSP-{String(d.id).padStart(3, "0")}
                            </span>
                          </div>
                          {d.description && (
                            <p className="gcTD-dispute-desc">
                              {d.description.length > 65
                                ? d.description.substring(0, 65) + "..."
                                : d.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="gcTD-dispute-right">
                        <span
                          className={`gcTD-days-badge ${DISPUTE_STATUS_CLASS[d.status?.toLowerCase()] ?? "gcTD-days-badge--orange"}`}
                        >
                          {d.status}
                        </span>
                        <span className="gcTD-chat-date">{formatDate(d.created_at)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

export default TeacherDashboardPage;